(ns ui.upload-status
  "The upload status card in the bottom right corner of every Replicant
   page. It tells the user what the quick upload
   (`storrito.upload.story-upload`) does with the picked or dropped
   files: which files it checks, which it uploads (with progress) and
   which it rejected, each with the reason and how to fix it.

   It does not block the page: the valid files upload while the user
   reads why the others were rejected. Rejections and failures stay until
   the user closes the card (close button or Esc).

   State in the store:

   - `:upload/status` `{:batch id, :checking n, :rejected [rejection
     ...], :failure message}`, see `start`, `checked` and `failed`
   - `:upload/active-uploads` the running uploads, mirrored from the
     upload manager by `track-uploads!`"
  (:require [app.upload-manager.core :as upload-manager]))

(defn start
  "The status when the upload `batch` (an id) of `file-count` files
   starts. A new upload replaces what the card showed about the previous
   one."
  [_status batch file-count]
  {:batch batch
   :checking file-count})

(defn checked
  "The status once the files of the `batch` are checked: the `rejected`
   files (see `storrito.upload.story-upload/rejection`) are listed until
   the user closes the card. A batch that a newer upload replaced
   changes nothing."
  [status batch rejected]
  (if (= batch (:batch status))
    (cond-> (dissoc status :checking)
      (seq rejected) (assoc :rejected (vec rejected)))
    status))

(defn failed
  "The status after the upload of the `batch` failed with the
   `message`. Also a batch that a newer upload replaced shows its
   failure, since its files may be missing in the gallery; only the
   check of the newer batch keeps running."
  [status batch message]
  (cond-> (assoc status :failure message)
    (= batch (:batch status)) (dissoc :checking)))

(defn file-count-text
  "\"1 file\", \"2 files\"."
  [n]
  (str n (if (= 1 n) " file" " files")))

(defn show-gallery-after-upload?
  "Whether the upload button of a schedule page opens the gallery after
   the upload `result` (see `storrito.upload.story-upload/
   handle-quick-story-files!`): only when files were saved, so rejected
   files and failures keep the user on the page with the card."
  [result pathname]
  (boolean (and (vector? result)
                (seq result)
                (contains? #{"/ui/agenda" "/ui/calendar"} pathname))))

(defn title
  "The headline of the card for the `status` and the running `uploads`."
  [{:keys [checking rejected failure]} uploads]
  (cond
    checking (str "Checking " (file-count-text checking) "…")
    (seq uploads) (str "Uploading " (file-count-text (count uploads)))
    failure "Upload failed"
    (some (comp nil? :file-name) rejected) "No files uploaded"
    (seq rejected) (str (file-count-text (count rejected)) " not uploaded")))

(defn visible?
  [state]
  (let [{:keys [checking rejected failure]} (:upload/status state)]
    (boolean (or checking
                 (seq (:upload/active-uploads state))
                 (seq rejected)
                 failure))))

(defn spinner
  []
  [:div
   {:class ["w-3" "h-3" "rounded-full" "border-2" "border-gray-300"
            "border-t-indigo-500" "animate-spin" "motion-reduce:animate-none"
            "shrink-0"]
    :aria-hidden "true"}])

(defn warning-icon
  [class]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 20 20"
         :fill "currentColor"
         :aria-hidden "true"
         :class (concat ["shrink-0"] class)}
   [:path {:fill-rule "evenodd"
           :clip-rule "evenodd"
           :d "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"}]])

(defn close-button
  []
  [:button
   {:type "button"
    :aria-label "Close"
    :title "Close (Esc)"
    :class ["-mr-1" "p-1" "rounded" "text-gray-500" "hover:text-gray-700"
            "hover:bg-gray-100" "focus:outline-none" "focus-visible:ring-2"
            "focus-visible:ring-indigo-500"]
    :on {:click [[:upload-status/close]]}}
   [:svg {:xmlns "http://www.w3.org/2000/svg"
          :fill "none"
          :viewBox "0 0 24 24"
          :stroke-width "2"
          :stroke "currentColor"
          :aria-hidden "true"
          :class ["w-4" "h-4"]}
    [:path {:stroke-linecap "round"
            :stroke-linejoin "round"
            :d "M6 18 18 6M6 6l12 12"}]]])

(defn header
  [{:keys [checking rejected failure] :as status} uploads]
  (let [busy? (or checking (seq uploads))]
    [:div
     {:class ["px-3" "py-2" "bg-gray-50" "border-b" "border-gray-200"
              "flex" "items-center" "gap-2"]}
     (if busy?
       (spinner)
       (warning-icon ["w-4" "h-4" "text-red-600"]))
     [:h2
      {:class ["text-xs" "font-semibold" "text-gray-700" "grow"]}
      (title status uploads)]
     (when (or (seq rejected) failure)
       (close-button))]))

(defn progress-row
  [[id upload]]
  (let [progress (* (or (:progress upload) 0) 100)]
    [:li
     {:class ["px-3" "py-2"]
      :replicant/key [::upload id]}
     [:div
      {:class ["flex" "items-center" "justify-between" "mb-1"]}
      [:span
       {:class ["text-xs" "text-gray-700" "truncate"]}
       (or (:name upload) "Uploading…")]
      [:span
       {:class ["text-xs" "font-medium" "text-gray-600" "ml-2" "shrink-0"
                "tabular-nums"]}
       (if (< progress 100)
         (str (.toFixed progress 0) "%")
         "Processing")]]
     [:div
      {:class ["w-full" "bg-gray-100" "rounded-full" "h-1.5" "overflow-hidden"]
       :role "progressbar"
       :aria-valuemin 0
       :aria-valuemax 100
       :aria-valuenow (js/Math.round progress)}
      [:div
       {:class (concat ["bg-indigo-500" "h-1.5" "rounded-full"]
                       (if (< progress 100)
                         ["transition-all" "duration-300"]
                         ["w-full" "animate-pulse" "motion-reduce:animate-none"]))
        :style (when (< progress 100)
                 {:width (str progress "%")})}]]]))

(defn rejection-row
  [i {:keys [file-name reason hint]}]
  [:li
   {:class ["px-3" "py-2" "flex" "gap-2"]
    :replicant/key [::rejected i]}
   (warning-icon ["w-4" "h-4" "mt-0.5" "text-red-600"])
   [:div
    {:class ["min-w-0"]}
    (when file-name
      [:p
       {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]
        :title file-name}
       file-name])
    [:p
     {:class ["text-xs" "text-red-700"]}
     reason]
    [:p
     {:class ["text-xs" "text-gray-600"]}
     hint]]])

(defn live-region
  "Always rendered (also without the card), so screen readers announce
   the title of the card when it appears or changes: a live region that
   is inserted with its content is often not announced."
  [state]
  [:div.sr-only
   {:role "status"
    :aria-live "polite"}
   (when (visible? state)
     (title (:upload/status state)
            (:upload/active-uploads state)))])

(defn render
  "The card, when there is something to tell about an upload."
  [state]
  (when (visible? state)
    (let [{:keys [rejected failure] :as status} (:upload/status state)
          uploads (sort-by (fn [[_id upload]]
                             (:added upload))
                           (:upload/active-uploads state))]
      [:section
       {:class ["fixed" "bottom-4" "right-4" "z-50" "w-80"
                "max-w-[calc(100vw-2rem)]" "bg-white" "rounded-lg" "shadow-xl"
                "border" "border-gray-200" "overflow-hidden"]
        :aria-label "Upload status"
        :on {:keydown [[:upload-status/keydown]]}}
       (header status uploads)
       [:div
        {:class ["max-h-72" "overflow-y-auto"]}
        (when failure
          [:div
           {:class ["px-3" "py-2" "bg-red-50" "border-b" "border-red-100"]
            :role "alert"}
           [:p
            {:class ["text-sm" "text-red-800"]}
            failure]
           [:p
            {:class ["text-xs" "text-gray-700" "mt-1"]}
            "Files that finished before the error are in your gallery. Try the others again, or contact support if it keeps failing."]])
        (when (seq uploads)
          [:ul
           {:class ["divide-y" "divide-gray-100"]}
           (map progress-row uploads)])
        (when (and (seq rejected)
                   (seq uploads))
          [:h3
           {:class ["px-3" "pt-2" "text-xs" "font-semibold" "text-gray-700"
                    "border-t" "border-gray-200"]}
           (str (file-count-text (count rejected)) " not uploaded")])
        (when (seq rejected)
          [:ul
           {:class ["divide-y" "divide-gray-100"]}
           (map-indexed rejection-row rejected)])]])))

(defn start!
  "Starts a new upload batch of `file-count` files and returns its id,
   which `checked!` and `failed!` take."
  [store file-count]
  (let [batch (random-uuid)]
    (swap! store update :upload/status start batch file-count)
    batch))

(defn checked!
  [store batch rejected]
  (swap! store update :upload/status checked batch rejected))

(defn failed!
  [store batch message]
  (swap! store update :upload/status failed batch message))

(defonce ^:private tracking
  (atom 0))

(defn visible-uploads
  "The running uploads of the upload manager that the user started
   (without the hidden derived ones like previews)."
  [upload-state]
  (into {}
        (remove (fn [[_id upload]]
                  (:hidden upload)))
        (:uploads upload-state)))

(defn track-uploads!
  "Mirrors the running uploads into `:upload/active-uploads` of the
   `store`, for the progress in the card, until the returned fn is
   called. Several uploads may be tracked at the same time; the progress
   is cleared when the last one stops."
  [store]
  (swap! tracking inc)
  (let [interval (js/setInterval
                   (fn []
                     (swap! store
                            assoc
                            :upload/active-uploads
                            (visible-uploads @upload-manager/upload-state)))
                   300)]
    (fn stop! []
      (js/clearInterval interval)
      (when (zero? (swap! tracking dec))
        (swap! store dissoc :upload/active-uploads)))))

(defn close!
  [{:keys [ui/store]}]
  (swap! store update :upload/status dissoc :rejected :failure))

(defn keydown!
  "Esc closes the card while the focus is inside it (e.g. on the close
   button)."
  [{:keys [replicant/js-event] :as w}]
  (when (= "Escape" (.-key js-event))
    (close! w)))

(def register
  [{:ui.action/kind :upload-status/close
    :ui.action/handler #'close!}
   {:ui.action/kind :upload-status/keydown
    :ui.action/handler #'keydown!}])
